// Hub de Reportes + P&L General mensual.
// Primera versión: agregados básicos sobre pagos (suscripciones) + pedidos one-shot.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const MESES_ATRAS = 6
// Comisión Handy ponderada — se lee en runtime de la DB
// (tabla comisiones_handy_config + comisiones_handy_planes vía RPC get_comision_handy_ponderada).
// Si la RPC falla, fallback a 2,665% (Clásico YA del v43, default razonable).
const FALLBACK_COMISION_HANDY_PCT = 0.02665
const FALLBACK_COSTO_FIJO_HANDY_TX = 14.64
const IVA_PCT = 0.22

function ymKey(d) {
  const dt = new Date(d)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
}

function ymLabel(ym) {
  const [y, m] = ym.split('-')
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Set','Oct','Nov','Dic']
  return `${meses[Number(m) - 1]} ${y}`
}

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`

export default function Reportes() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagos, setPagos] = useState([])
  const [pedidos, setPedidos] = useState([])
  // Comisión Handy leída de DB (la edita Roberto desde /admin/finanzas/comisiones-handy)
  const [comisionHandyPct, setComisionHandyPct] = useState(FALLBACK_COMISION_HANDY_PCT)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      // Últimos 6 meses
      const desde = new Date()
      desde.setMonth(desde.getMonth() - MESES_ATRAS)
      desde.setDate(1)
      const desdeStr = desde.toISOString().slice(0, 10)

      // Cargar comisión Handy actual (configurable por Roberto desde /admin/finanzas/comisiones-handy)
      const { data: comHandy } = await supabase.rpc('get_comision_handy_ponderada')
      if (comHandy?.ponderado_pct) setComisionHandyPct(Number(comHandy.ponderado_pct) / 100)

      const [{ data: pa, error: pe }, { data: pd, error: pde }] = await Promise.all([
        // Reportes: solo datos reales (excluye es_prueba)
        supabase
          .from('pagos')
          .select('id, monto, estado, pagado_at, created_at, metodo')
          .eq('estado', 'pagado')
          .eq('es_prueba', false)
          .gte('pagado_at', desdeStr),
        supabase
          .from('pedidos')
          .select('id, monto_total, estado, tipo, metodo_pago, pagado_at, created_at')
          .in('estado', ['pagado', 'en_armado', 'en_ruta', 'entregado'])
          .eq('es_prueba', false)
          .gte('created_at', desdeStr),
      ])
      if (pe) throw pe
      if (pde) throw pde
      setPagos(pa || [])
      setPedidos(pd || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // Agregado por mes
  const filas = useMemo(() => {
    const map = new Map()
    function ensure(ym) {
      if (!map.has(ym)) map.set(ym, {
        ym,
        ingresos_susc: 0, n_susc: 0,
        ingresos_oneshot: 0, n_oneshot: 0,
        ingresos_handy: 0,  // los que pasaron por MP, para comisión estimada
      })
      return map.get(ym)
    }
    // Detectamos pagos procesados por Handy/Plexo (vs transferencia bancaria B2B).
    // Histórico nunca llegó a MP — el flag 'mp' / 'mercadopago' queda solo por compatibilidad
    // de datos legacy en la tabla. B2B paga por transferencia bancaria a cuenta Limpack
    // → no carga Handy (decisión 01-jun-2026).
    const esHandy = (m) => {
      if (!m) return false
      const v = m.toLowerCase()
      return v.startsWith('handy') || v.startsWith('plexo') || v === 'tarjeta'
    }
    pagos.forEach(p => {
      const ym = ymKey(p.pagado_at || p.created_at)
      const row = ensure(ym)
      row.ingresos_susc += Number(p.monto || 0)
      row.n_susc += 1
      if (esHandy(p.metodo)) {
        row.ingresos_handy += Number(p.monto || 0)
      }
    })
    pedidos.forEach(p => {
      const ym = ymKey(p.pagado_at || p.created_at)
      const row = ensure(ym)
      row.ingresos_oneshot += Number(p.monto_total || 0)
      row.n_oneshot += 1
      if (esHandy(p.metodo_pago)) {
        row.ingresos_handy += Number(p.monto_total || 0)
      }
    })
    // Calcular comisiones e IVA + neto estimado
    map.forEach(r => {
      r.ingresos_total = r.ingresos_susc + r.ingresos_oneshot
      r.comision_handy = r.ingresos_handy * comisionHandyPct
      r.iva_debito = r.ingresos_total - (r.ingresos_total / (1 + IVA_PCT))   // IVA incluido en precio
      r.neto_estimado = r.ingresos_total - r.comision_handy
    })
    return Array.from(map.values()).sort((a, b) => b.ym.localeCompare(a.ym))
  }, [pagos, pedidos, comisionHandyPct])

  const totales = useMemo(() => {
    const t = { ingresos_total: 0, ingresos_susc: 0, ingresos_oneshot: 0, comision_handy: 0, iva_debito: 0, neto_estimado: 0, n_susc: 0, n_oneshot: 0 }
    filas.forEach(r => {
      t.ingresos_total += r.ingresos_total
      t.ingresos_susc += r.ingresos_susc
      t.ingresos_oneshot += r.ingresos_oneshot
      t.comision_handy += r.comision_handy
      t.iva_debito += r.iva_debito
      t.neto_estimado += r.neto_estimado
      t.n_susc += r.n_susc
      t.n_oneshot += r.n_oneshot
    })
    return t
  }, [filas])

  return (
    <AdminLayout>
      <div className="page">
        <h1>📊 Reportes</h1>
        <p className="muted">Visión financiera y operativa. Datos de los últimos {MESES_ATRAS} meses.</p>

        {/* Tarjetas de navegación a sub-reportes */}
        <div className="rep-section-title">📈 Suscripciones / Crecimiento</div>
        <div className="rep-nav">
          <Link to="/admin/reportes/mrr-churn" className="rep-card">
            <div className="rep-card-icon">📈</div>
            <div>
              <div className="rep-card-title">MRR + Churn</div>
              <div className="rep-card-sub">Ingreso recurrente y tasa de cancelación</div>
            </div>
          </Link>
          <Link to="/admin/reportes/cohort-ltv" className="rep-card">
            <div className="rep-card-icon">👥</div>
            <div>
              <div className="rep-card-title">Cohort + LTV</div>
              <div className="rep-card-sub">Retención por cohorte y valor de vida</div>
            </div>
          </Link>
          <Link to="/admin/reportes/vecinos" className="rep-card">
            <div className="rep-card-icon">🏢</div>
            <div>
              <div className="rep-card-title">Vecinos / Referidos</div>
              <div className="rep-card-sub">Performance del programa de referidos</div>
            </div>
          </Link>
        </div>

        <div className="rep-section-title">🏆 Rentabilidad</div>
        <div className="rep-nav">
          <Link to="/admin/reportes/rentabilidad-producto" className="rep-card">
            <div className="rep-card-icon">🏆</div>
            <div>
              <div className="rep-card-title">Rentabilidad por producto</div>
              <div className="rep-card-sub">Margen unitario y volumen vendido</div>
            </div>
          </Link>
          <Link to="/admin/reportes/rentabilidad-barrio" className="rep-card">
            <div className="rep-card-icon">🗺️</div>
            <div>
              <div className="rep-card-title">Rentabilidad por barrio</div>
              <div className="rep-card-sub">Ingresos y entregas por zona</div>
            </div>
          </Link>
          <Link to="/admin/reportes/stock-turnover" className="rep-card">
            <div className="rep-card-icon">📦</div>
            <div>
              <div className="rep-card-title">Stock turnover</div>
              <div className="rep-card-sub">Días de cobertura, rotación y deadstock</div>
            </div>
          </Link>
        </div>

        <div className="rep-section-title">🧾 Contabilidad / Finanzas</div>
        <div className="rep-nav">
          <Link to="/admin/finanzas" className="rep-card">
            <div className="rep-card-icon">📒</div>
            <div>
              <div className="rep-card-title">Contabilidad</div>
              <div className="rep-card-sub">Dashboard contable + asientos + plan de cuentas</div>
            </div>
          </Link>
          <Link to="/admin/reportes/iva" className="rep-card">
            <div className="rep-card-icon">🧾</div>
            <div>
              <div className="rep-card-title">IVA mensual (DGI)</div>
              <div className="rep-card-sub">Débito − crédito = saldo a presentar</div>
            </div>
          </Link>
        </div>

        <h2 style={{ marginTop: 32, color: '#1a3461' }}>📈 P&L General — últimos {MESES_ATRAS} meses</h2>

        {/* Totales acumulados */}
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Ingresos totales</h3>
            <div className="stat">{fmtUYU(totales.ingresos_total)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>IVA incluido</div>
          </div>
          <div className="card">
            <h3>Suscripciones</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(totales.ingresos_susc)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{totales.n_susc} cobros</div>
          </div>
          <div className="card">
            <h3>One-shot</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{fmtUYU(totales.ingresos_oneshot)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{totales.n_oneshot} pedidos</div>
          </div>
          <div className="card">
            <h3>Comisiones procesador (est.)</h3>
            <div className="stat" style={{ color: '#ef4444' }}>{fmtUYU(totales.comision_handy)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>MP · ~{(comisionHandyPct * 100).toFixed(3)}%</div>
          </div>
          <div className="card">
            <h3>IVA débito (en precio)</h3>
            <div className="stat" style={{ color: '#f59e0b' }}>{fmtUYU(totales.iva_debito)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>22% sobre ingresos</div>
          </div>
          <div className="card">
            <h3>Neto estimado</h3>
            <div className="stat" style={{ color: '#1a3461' }}>{fmtUYU(totales.neto_estimado)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Antes de IVA y costos</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>📭</div>
            <h3>Sin datos en los últimos {MESES_ATRAS} meses</h3>
          </div>
        )}

        {!loading && filas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th style={{ textAlign: 'right' }}>Ing. Suscripciones</th>
                  <th style={{ textAlign: 'right' }}>Ing. One-shot</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Comisión procesador est.</th>
                  <th style={{ textAlign: 'right' }}>Neto est.</th>
                </tr>
              </thead>
              <tbody>
                {filas.map(r => (
                  <tr key={r.ym}>
                    <td style={{ fontWeight: 700, color: '#1a3461' }}>{ymLabel(r.ym)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtUYU(r.ingresos_susc)}
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.n_susc} cobros</div>
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtUYU(r.ingresos_oneshot)}
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.n_oneshot} pedidos</div>
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtUYU(r.ingresos_total)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#ef4444' }}>−{fmtUYU(r.comision_handy)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#10B981' }}>{fmtUYU(r.neto_estimado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AnalisisIA
          reportType="pl_general"
          data={{ totales, serie: filas.slice(0, 6) }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> Comisión procesador (MP · MCC 5411) estimada al {(comisionHandyPct * 100).toFixed(3)}% — editable desde /admin/finanzas/comisiones-handy (nombre legacy). Desde 3-jul-2026 MP es el procesador único; Handy/Plexo quedaron standby.
          El neto NO descuenta costo de mercadería ni gastos operativos (envío, insumos).
          Para reportes detallados al contador usá los sub-reportes de arriba.
        </div>
      </div>

      <style>{`
        .rep-section-title { font-size: 13px; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; font-weight: 700; margin: 18px 0 10px; }
        .rep-nav { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; margin-bottom: 16px; }
        .rep-card {
          display: flex; align-items: center; gap: 14px;
          padding: 16px; background: #fff;
          border: 1px solid #e5e7eb; border-radius: 12px;
          text-decoration: none; color: inherit;
          transition: all .15s;
        }
        .rep-card:hover { border-color: #1a3461; background: #f8fafc; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(15,23,42,.08); }
        .rep-card-icon { font-size: 32px; }
        .rep-card-title { font-weight: 700; color: #1a3461; font-size: 15px; }
        .rep-card-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th {
          text-align: left; padding: 12px 14px;
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 12px; text-transform: uppercase; color: #6b7280;
          font-weight: 700; letter-spacing: .03em;
        }
        .usu-table tbody td {
          padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top;
        }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}
